import React, { useEffect, useLayoutEffect, useState } from 'react';
import { CheckCircle2, Circle, Trophy, Play, Camera, Share2, Ruler, X, ArrowRight, Sparkles } from 'lucide-react';

const TOUR_KEY = 'offersz_tour_v1';
const DISMISS_KEY = 'offersz_setup_dismissed';
const ICONS = { photo: Camera, offer: Trophy, video: Play, stats: Ruler, share: Share2 };

const STEPS = [
  { sel: '[data-testid="get-started-card"]', title: 'Your setup checklist', body: "Knock these out and your page is ready for college coaches. Tap any item to jump straight to it." },
  { sel: '[data-testid="copy-link-btn"]', title: 'Share your link', body: 'Copy your Offersz link here, then drop it in your X bio and socials so coaches can find you.' },
  { sel: '[data-testid="dashboard-view-profile"]', title: 'See what coaches see', body: 'Preview your public page any time with View Profile.' },
  { sel: '[data-testid="dashboard-tabs"]', title: 'Build it out', body: 'Add offers, highlights, stats and measurables from these tabs.' },
];

const TourTooltip = ({ rect, step, idx, total, onNext, onBack, onSkip }) => {
  const vh = window.innerHeight;
  const below = rect.top + rect.height + 14;
  const placeBelow = below < vh - 200;
  const top = placeBelow ? below : Math.max(12, rect.top - 190);
  const left = Math.min(Math.max(12, rect.left), (window.innerWidth || 360) - 332);
  return (
    <div className="absolute z-[301] w-80 bg-white text-gray-900 rounded-2xl shadow-2xl p-4" style={{ top, left }} data-testid="tour-tooltip">
      <p className="font-black text-base mb-1">{step.title}</p>
      <p className="text-sm text-gray-600 mb-4 leading-snug">{step.body}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400 font-semibold">{idx + 1} / {total}</span>
        <div className="flex items-center gap-2">
          <button onClick={onSkip} data-testid="tour-skip" className="text-xs font-semibold text-gray-400 hover:text-gray-600 px-2">Skip</button>
          {idx > 0 && <button onClick={onBack} data-testid="tour-back" className="text-xs font-bold text-gray-600 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200">Back</button>}
          <button onClick={onNext} data-testid="tour-next" className="text-xs font-bold text-white px-4 py-1.5 rounded-full bg-[#16A34A] hover:bg-[#15803d]">{idx === total - 1 ? 'Done' : 'Next'}</button>
        </div>
      </div>
    </div>
  );
};

export const GetStarted = ({ darkMode, tasks, onTask, enableTour = true }) => {
  const doneCount = tasks.filter(t => t.done).length;
  const allDone = doneCount === tasks.length;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');

  const [tourOn, setTourOn] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!enableTour || allDone) return;
    if (localStorage.getItem(TOUR_KEY) === '1') return;
    const t = setTimeout(() => setTourOn(true), 900);
    return () => clearTimeout(t);
  }, [enableTour, allDone]);

  const endTour = () => { setTourOn(false); localStorage.setItem(TOUR_KEY, '1'); };

  useLayoutEffect(() => {
    if (!tourOn) return;
    const compute = () => {
      const el = document.querySelector(STEPS[stepIdx]?.sel);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    const el = document.querySelector(STEPS[stepIdx]?.sel);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const t = setTimeout(compute, 350);
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => { clearTimeout(t); window.removeEventListener('resize', compute); window.removeEventListener('scroll', compute, true); };
  }, [tourOn, stepIdx]);

  const next = () => { if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1); else endTour(); };
  const back = () => setStepIdx(Math.max(0, stepIdx - 1));

  const cardHidden = allDone || dismissed;

  return (
    <>
      {!cardHidden && (
        <div data-testid="get-started-card" className={`rounded-2xl border p-4 sm:p-5 mb-4 ${darkMode ? 'bg-[#16A34A]/[0.07] border-[#16A34A]/25' : 'bg-[#16A34A]/[0.06] border-[#16A34A]/20'}`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
              <div>
                <p className="font-black text-base leading-tight">Get started</p>
                <p className={`text-xs ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>{doneCount} of {tasks.length} done — finish setting up your page</p>
              </div>
            </div>
            <button onClick={() => { setDismissed(true); localStorage.setItem(DISMISS_KEY, '1'); }} data-testid="get-started-dismiss" className={`p-1.5 rounded-full flex-shrink-0 ${darkMode ? 'text-white/30 hover:bg-white/10' : 'text-gray-400 hover:bg-gray-100'}`}><X className="h-4 w-4" /></button>
          </div>
          <div className={`h-1.5 rounded-full mb-3 ${darkMode ? 'bg-white/10' : 'bg-gray-200'}`}>
            <div className="h-full rounded-full bg-[#16A34A] transition-[width] duration-500" style={{ width: `${Math.round(100 * doneCount / tasks.length)}%` }} />
          </div>
          <div className="space-y-1">
            {tasks.map(t => {
              const Icon = ICONS[t.key] || Circle;
              return (
                <button key={t.key} onClick={() => !t.done && onTask(t.key)} data-testid={`task-${t.key}`} disabled={t.done}
                  className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${t.done ? 'opacity-60 cursor-default' : darkMode ? 'hover:bg-white/5' : 'hover:bg-white/70'}`}>
                  {t.done ? <CheckCircle2 className="h-5 w-5 text-[#16A34A] flex-shrink-0" /> : <Circle className={`h-5 w-5 flex-shrink-0 ${darkMode ? 'text-white/25' : 'text-gray-300'}`} />}
                  <Icon className={`h-4 w-4 flex-shrink-0 ${t.done ? 'text-[#16A34A]' : darkMode ? 'text-white/50' : 'text-gray-500'}`} />
                  <span className={`flex-1 text-sm font-semibold ${t.done ? 'line-through' : ''}`}>{t.label}</span>
                  {!t.done && <ArrowRight className={`h-4 w-4 ${darkMode ? 'text-white/30' : 'text-gray-400'}`} />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tourOn && rect && (
        <div className="fixed inset-0 z-[300]" data-testid="onboarding-tour">
          <div className="absolute inset-0" onClick={endTour} />
          <div className="absolute rounded-xl ring-2 ring-[#16A34A] transition-all duration-300 pointer-events-none"
            style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12, boxShadow: '0 0 0 9999px rgba(0,0,0,0.62)' }} />
          <TourTooltip rect={rect} step={STEPS[stepIdx]} idx={stepIdx} total={STEPS.length} onNext={next} onBack={back} onSkip={endTour} />
        </div>
      )}
    </>
  );
};

export default GetStarted;
